import React from "react";
import { Container, Typography, Box, Divider, Grid, Paper, Tooltip, LinearProgress, useTheme } from "@mui/material";
import SectionTitle from "../../components/layouts/others/title-section.layout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowDown,
    faArrowUp,
    faChartBar,
    faMoneyBillWave,
    faUserTie,
    faUniversity,
    faGavel,
    faPiggyBank,
    faTriangleExclamation,
    faChartLine,
    faExclamationCircle
} from "@fortawesome/free-solid-svg-icons";

const barChartData = [
    { year: 2017, value: 0 },
    { year: 2018, value: 0 },
    { year: 2019, value: 0 },
    { year: 2020, value: 5 },
    { year: 2021, value: 15 },
    { year: 2022, value: 20 },
    { year: 2023, value: 25 },
    { year: 2024, value: 30 }
];

const debtData = [
    { year: 2021, value: 100 },
    { year: 2022, value: 200 },
    { year: 2023, value: 600 },
    { year: 2024, value: 800 }
];

const TaxBarChart: React.FC = () => {
    const theme = useTheme();
    const height = 120;
    const yTicks = [0, 5, 10, 15, 20, 25, 30];

    return (
        <Box sx={{ mb: 4, mt: 3 }}>
            <Typography variant="subtitle2" align="center" color="text.secondary" gutterBottom>
                <FontAwesomeIcon icon={faChartBar} color={theme.palette.primary.main} />
                Évolution annuelle de la taxe foncière (%)
            </Typography>
            <Box display="flex" sx={{ maxWidth: 440, mx: "auto" }}>
                {/* Axe vertical gauche */}
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                    alignItems="flex-end"
                    height={height}
                    width={28}
                    pt={0.8}
                    pb={2.3}
                >
                    {yTicks.slice().reverse().map((v) => (
                        <Typography
                            key={v}
                            variant="caption"
                            sx={{
                                color: theme.palette.grey[600],
                                fontSize: 12,
                                minHeight: 0,
                            }}
                        >
                            {v}%
                        </Typography>
                    ))}
                </Box>
                {/* Zone du graphique avec fond et grille */}
                <Box sx={{ px: 1, flex: 1, position: "relative" }}>
                    <svg width="100%" height={height + 20} viewBox={`0 0 400 ${height + 20}`}>
                        {/* Grille horizontale */}
                        {yTicks.map((v, i) => (
                            <line
                                key={v}
                                x1={0}
                                x2={400}
                                y1={13 + ((height - 10) * (1 - v / 30))}
                                y2={13 + ((height - 10) * (1 - v / 30))}
                                stroke={i === 0 ? theme.palette.grey[400] : theme.palette.grey[200]}
                                strokeDasharray={i === 0 ? undefined : "2,2"}
                                strokeWidth={i === 0 ? 1.2 : 1}
                            />
                        ))}
                        {/* Les barres */}
                        {barChartData.map((data, idx) => {
                            const x = (idx * (400 / barChartData.length)) + 17;
                            const w = 25;
                            const y = 13 + ((height - 10) * (1 - data.value / 30));
                            const h = ((height - 10) * data.value / 30);
                            return (
                                <rect
                                    key={data.year}
                                    x={x}
                                    y={y}
                                    width={w}
                                    height={h}
                                    rx={6}
                                    fill={theme.palette.primary.main}
                                    opacity={data.value === 0 ? 0.25 : 1}
                                />
                            );
                        })}
                        {/* Les années en bas */}
                        {barChartData.map((data, idx) => (
                            <text
                                key={data.year}
                                x={(idx * (400 / barChartData.length)) + 29}
                                y={height + 17}
                                textAnchor="middle"
                                style={{ fontSize: 12, fontFamily: "inherit", fill: theme.palette.text.secondary }}
                            >{data.year}</text>
                        ))}
                    </svg>
                </Box>
            </Box>
        </Box>
    );
};


// Simple LineChart for Debt Evolution
const DebtLineChart: React.FC = () => {
    const theme = useTheme();
    const chartHeight = 120;
    const chartWidth = 270;
    const yTicks = [0, 200, 400, 600, 800];

    // calcul des points
    const points = debtData.map((d, i) => {
        const x = (chartWidth / (debtData.length - 1)) * i + 8;
        const y = 10 + ((chartHeight - 20) * (1 - d.value / 800));
        return { x, y, year: d.year, value: d.value };
    });

    return (
        <Box sx={{ my: 4 }}>
            <Typography variant="subtitle2" align="center" color="text.secondary" gutterBottom>
                <FontAwesomeIcon icon={faChartLine} color={theme.palette.primary.main} />
                Encours de la dette par habitant (€)
            </Typography>
            <Box display="flex" sx={{ maxWidth: 380, mx: "auto" }}>
                {/* Axe vertical gauche */}
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                    alignItems="flex-end"
                    height={chartHeight}
                    width={33}
                    pt={0.5}
                    pb={2}
                >
                    {yTicks.slice().reverse().map((v) => (
                        <Typography
                            key={v}
                            variant="caption"
                            sx={{
                                color: theme.palette.grey[600],
                                fontSize: 12,
                                minHeight: 0,
                            }}
                        >
                            {v}
                            <tspan style={{ fontSize: 10 }}>€</tspan>
                        </Typography>
                    ))}
                </Box>
                {/* Graphique ligne */}
                <Box sx={{ flex: 1, position: "relative" }}>
                    <svg width={chartWidth + 30} height={chartHeight + 28}>
                        {/* Grilles horizontales */}
                        {yTicks.map((v) => (
                            <line
                                key={v}
                                x1={15}
                                x2={chartWidth + 18}
                                y1={10 + ((chartHeight - 20) * (1 - v / 800))}
                                y2={10 + ((chartHeight - 20) * (1 - v / 800))}
                                stroke={theme.palette.grey[200]}
                                strokeWidth={1}
                                strokeDasharray="3 2"
                            />
                        ))}
                        {/* Ligne */}
                        <polyline
                            fill="none"
                            stroke={theme.palette.primary.dark}
                            strokeWidth={3}
                            points={points.map((p) => `${p.x},${p.y}`).join(" ")}
                        />
                        {/* Points */}
                        {points.map((p, idx) => (
                            <circle
                                key={p.year}
                                cx={p.x}
                                cy={p.y}
                                r={7}
                                fill={theme.palette.warning.main}
                                stroke="#fff"
                                strokeWidth={2}
                            />
                        ))}
                        {/* Labels d'année (X axis) */}
                        {points.map((p, idx) => (
                            <text
                                key={p.year}
                                x={p.x}
                                y={chartHeight + 22}
                                textAnchor="middle"
                                fontSize={12}
                                fill={theme.palette.text.secondary}
                                fontFamily="inherit"
                            >
                                {p.year}
                            </text>
                        ))}
                    </svg>
                </Box>
            </Box>
        </Box>
    );
};

const POST20240415: React.FC = () => {
    return (
        <Container sx={{ mb: 5 }}>
            <SectionTitle title={"ESPACE INFORMATIONS DES HABITANTS"} />
            <Box mt={3}>
                <Typography
                    variant="h6"
                    fontWeight={700}
                    align="center"
                    gutterBottom
                    color="primary"
                >
                    2018 - 2024 : Chronique d’une faillite financière et démocratique annoncée
                    <FontAwesomeIcon icon={faExclamationCircle} style={{ marginLeft: 8, color: "#d32f2f" }} />
                </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography gutterBottom>
                <strong>En 2018,</strong> à la prise de fonction de la municipalité actuelle, la commune disposait d’une <strong>trésorerie nette excédentaire</strong>, permettant les investissements attendus, <strong>sans aucune augmentation d’impôt foncier ni recours à l’emprunt bancaire</strong>.
                <br />
                <strong>Aujourd’hui,</strong> les caisses de la commune sont <span style={{ color: "#d32f2f", fontWeight: 600 }}>vides</span>.
            </Typography>

            <Box sx={{ background: "#fffde7", p: 2, borderRadius: 2, mt: 2, mb: 2 }}>
                <Typography variant="body1" fontWeight={600} color="warning.main" gutterBottom>
                    <FontAwesomeIcon icon={faTriangleExclamation} style={{ marginRight: 6 }} />
                    Dépenses anormalement élevées...
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                    <li>De nombreuses « études » sous-traitées à des cabinets de conseil, sans retombée concrète.</li>
                    <li>Aucune rentabilité ni amélioration mesurable pour la collectivité.</li>
                </ul>
            </Box>

            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper variant="outlined" sx={{ p: 2, mb: 2, background: "#f9fbe7" }}>
                        <Typography fontWeight={700} color="primary" gutterBottom>
                            <FontAwesomeIcon icon={faMoneyBillWave} style={{ marginRight: 8 }} />
                            Les réserves et l’excédent… <span style={{ color: "#d32f2f" }}>dilapidés</span>
                        </Typography>
                        <Typography gutterBottom fontSize={15}>
                            <FontAwesomeIcon icon={faPiggyBank} /> Fonds propres : <strong>5,6 M€</strong> en 2018
                            <FontAwesomeIcon icon={faArrowDown} color="#b71c1c" style={{ margin: "0 4px" }}/> <strong>200 k€</strong> en 2024 ({'–'}96%)
                        </Typography>
                        <Typography gutterBottom fontSize={15}>
                            <FontAwesomeIcon icon={faUniversity} /> Excédent budgétaire : <strong>1 M€</strong> en 2018
                            <FontAwesomeIcon icon={faArrowDown} color="#b71c1c" style={{ margin: "0 4px" }}/> <strong>0 €</strong> en 2024 ({'–'}100%)
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            Source : Trésor Public – Bénéfices et réserves issues des précédentes mandatures.
                        </Typography>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper variant="outlined" sx={{ p: 2, mb: 2, background: "#f3e5f5" }}>
                        <Typography fontWeight={700} color="secondary" gutterBottom>
                            <FontAwesomeIcon icon={faMoneyBillWave} style={{ marginRight: 8 }} />
                            Des dépenses... <span style={{ color: "#6d1b7b" }}>exorbitantes</span>
                        </Typography>
                        <Typography fontSize={15} gutterBottom>
                            <FontAwesomeIcon icon={faUserTie} /> Dépenses de personnel : <strong>2,8 M€</strong> (2018)
                            <FontAwesomeIcon icon={faArrowUp} color="#2e7d32" style={{ margin: "0 4px" }}/> <strong>4,2 M€</strong> (2024) (+50%)
                        </Typography>
                        <Typography fontSize={15} gutterBottom>
                            <FontAwesomeIcon icon={faGavel} /> Charges générales de fonctionnement : <strong>1,4 M€</strong> (2018)
                            <FontAwesomeIcon icon={faArrowUp} color="#2e7d32" style={{ margin: "0 4px" }}/> <strong>2,2 M€</strong> (2024) (+57%)
                        </Typography>
                        <Typography fontSize={15} gutterBottom>
                            Cabinets de conseil (ex : Projet BLR2030) : <strong>+300 k€</strong> depuis 2021
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ background: "#e3f2fd", p: 2, borderRadius: 2, mb: 2 }}>
                <Typography fontWeight={700} color="info.main" gutterBottom>
                    <FontAwesomeIcon icon={faExclamationCircle} style={{ marginRight: 6 }} />
                    Mesures du Maire :
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                    <li><strong>Hausse massive des impôts fonciers</strong> (+40% en 3 ans)</li>
                    <li>Endettement accru (dette multipliée par 9 entre 2022 & 2024)</li>
                    <li>Arrêt net des projets du programme électoral</li>
                    <li>Baisses des subventions aux associations (-10%) & écoles (-30%)</li>
                </ul>
            </Box>

            <TaxBarChart />
            <Typography variant="caption" color="textSecondary" display="block" align="right">
                Source : Nos avis d'imposition foncière, Trésor Public
            </Typography>

            <Typography sx={{ fontStyle: "italic", mt: 2 }}>
                <FontAwesomeIcon icon={faExclamationCircle} color="#ed6c02" /> Hausse attendue d’environ 500 € par avis de taxe foncière en 2024 (~26%).
            </Typography>

            <DebtLineChart />
            <Typography variant="caption" color="textSecondary" display="block" align="right" sx={{ mb: 2 }}>
                Source : Rapport d’Orientations Budgétaires 2024 – équipe municipale
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ background: "#fff3e0", p: 2, borderRadius: 2, mb: 2 }}>
                <Typography fontWeight={600} color="warning.main" mb={1}>
                    <FontAwesomeIcon icon={faTriangleExclamation} style={{ marginRight: 6 }} />
                    Les investissements annoncés : tous annulés, reportés ou non réalisés
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                    <li>Crèche : projet abandonné <span style={{ fontWeight: 500 }}>&gt;150 k€ perdus</span></li>
                    <li>Équipements sportifs : projet annulé = <span style={{ fontWeight: 500 }}>160 k€ perdus</span></li>
                    <li>Bâtiment « maison médicale » : construction reportée à 2025 au mieux</li>
                    <li>Renforcement fondations de l’église : reporté après 2026 (édifice se dégrade)</li>
                    <li>Médiathèque : seul projet en cours malgré un recours juridique</li>
                </ul>
            </Box>

            <Typography sx={{ mb: 2 }}>
                Pour <strong>justifier son bilan</strong>, le Maire utilise le chantage à la réélection en 2026 pour faire avancer les projets jamais réalisés.
                <br />
                Aucun plan clair pour l’habitat social, aucune stratégie partagée sur les objectifs SRU triennaux.
            </Typography>

            <Box sx={{ background: "#f9dbe6", p: 2, borderRadius: 2, mb: 2 }}>
                <Typography color="error" fontWeight={700} gutterBottom>
                    <FontAwesomeIcon icon={faExclamationCircle} style={{ marginRight: 6 }} />
                    Gestion sans contrôle & démocratie mise à mal
                </Typography>
                <Typography>
                    Plus aucun élu « garde-fou » ; tous ont démissionné.<br/>
                    Décisions prises par le Maire seul, sans information ni consultation réelle des habitants.
                </Typography>
            </Box>

            <Typography sx={{ fontWeight: 700, color: "primary.main", mt: 2, mb: 1 }}>
                <FontAwesomeIcon icon={faTriangleExclamation} style={{ marginRight: 8 }} />
                Le bilan : impôts et dettes explosent, investissements nécessaires absents, l’avenir de la commune menacé.
            </Typography>

            <Typography variant="caption" color="textSecondary">
                Après avoir plongé la commune dans une situation financière difficile, la stratégie du Maire demeure : <strong>« Les Bacots ont de l’argent, ils paieront... »</strong>
            </Typography>
        </Container>
    );
};

export default POST20240415;